import { useState, useEffect, useCallback } from 'react'
import { appsyncQuery } from '../../api/appsync-client.js'
import { useSubscription } from '../../api/useSubscription.js'

const REFLECTION_FIELDS = `
  reflectionId ownerUserId managerId createdBy
  title periodStart periodEnd
  status readyAt sharedAt closedAt
  successes challenges sideQuests forwardLook
  managerNotes
  goalSnapshotsAtClose subgoalSnapshotsAtClose
  createdAt updatedAt
`

// AWSJSON fields come back as JSON strings and must be sent as JSON strings.
const AWSJSON_FIELDS = [
  'successes',
  'challenges',
  'sideQuests',
  'forwardLook',
  'managerNotes',
  'goalSnapshotsAtClose',
  'subgoalSnapshotsAtClose',
]

function parseReflection(r) {
  if (!r) return r
  const out = { ...r }
  for (const k of AWSJSON_FIELDS) {
    if (typeof out[k] === 'string') {
      try { out[k] = JSON.parse(out[k]) } catch { /* keep as-is */ }
    }
  }
  return out
}

function stringifyReflectionInput(input) {
  if (!input) return input
  const out = { ...input }
  for (const k of AWSJSON_FIELDS) {
    if (k in out && out[k] !== null && typeof out[k] !== 'string') {
      out[k] = JSON.stringify(out[k])
    }
  }
  return out
}

const LIST_REFLECTIONS = `
  query ListReflections($ownerUserId: ID, $status: String) {
    listReflections(ownerUserId: $ownerUserId, status: $status) {
      ${REFLECTION_FIELDS}
    }
  }
`

const CREATE_REFLECTION = `
  mutation CreateReflection($input: CreateReflectionInput!) {
    createReflection(input: $input) {
      ${REFLECTION_FIELDS}
    }
  }
`

const DELETE_REFLECTION = `
  mutation DeleteReflection($reflectionId: ID!, $ownerUserId: ID) {
    deleteReflection(reflectionId: $reflectionId, ownerUserId: $ownerUserId)
  }
`

const ON_REFLECTION_CHANGED = `
  subscription OnReflectionChanged($ownerUserId: ID!) {
    onReflectionChanged(ownerUserId: $ownerUserId) {
      ${REFLECTION_FIELDS}
    }
  }
`

export { REFLECTION_FIELDS, parseReflection, stringifyReflectionInput }

export function useReflections({ ownerUserId = null, enabled = true } = {}) {
  const [reflections, setReflections] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!enabled) return
    setLoading(true)
    setError(null)
    try {
      const data = await appsyncQuery(LIST_REFLECTIONS, { ownerUserId })
      setReflections((data.listReflections || []).map(parseReflection))
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [ownerUserId, enabled])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!enabled) return
    const handler = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [enabled, load])

  useSubscription(
    ON_REFLECTION_CHANGED,
    { ownerUserId },
    (data) => {
      const updated = parseReflection(data.onReflectionChanged)
      if (!updated) return
      setReflections((prev) => {
        const exists = prev.some((r) => r.reflectionId === updated.reflectionId)
        if (exists) return prev.map((r) => (r.reflectionId === updated.reflectionId ? updated : r))
        return [...prev, updated]
      })
    },
    { enabled: enabled && !!ownerUserId },
  )

  const createReflection = useCallback(async (body) => {
    const input = { ...body }
    if (ownerUserId) input.ownerUserId = ownerUserId
    const data = await appsyncQuery(CREATE_REFLECTION, { input })
    const reflection = parseReflection(data.createReflection)
    setReflections((prev) => [reflection, ...prev])
    return reflection
  }, [ownerUserId])

  const deleteReflection = useCallback(async (reflectionId) => {
    await appsyncQuery(DELETE_REFLECTION, { reflectionId, ownerUserId })
    setReflections((prev) => prev.filter((r) => r.reflectionId !== reflectionId))
  }, [ownerUserId])

  return {
    reflections,
    loading,
    error,
    reload: load,
    createReflection,
    deleteReflection,
  }
}
